// Export utility functions for intelligence data

export type ExportFormat = 'csv' | 'json' | 'pdf';

/**
 * Convert data array to CSV format
 */
export const convertToCSV = (data: any[], headers?: string[]): string => {
  if (!data || data.length === 0) return '';
  
  const cols = headers || Object.keys(data[0]);
  
  const headerRow = cols.map(col => `"${col}"`).join(',');
  
  const dataRows = data.map(row => {
    return cols.map(col => {
      const value = row[col];
      
      if (value == null) return '""';
      
      if (Array.isArray(value)) {
        return `"${value.join('; ')}"`;
      }
      
      if (typeof value === 'object') {
        return `"${JSON.stringify(value)}"`;
      }
      
      const stringValue = String(value);
      if (stringValue.includes('"') || stringValue.includes(',') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      
      return `"${stringValue}"`;
    }).join(',');
  }).join('\n');
  
  return `${headerRow}\n${dataRows}`;
};

/**
 * Convert data to JSON format (pretty printed)
 */
export const convertToJSON = (data: any[]): string => {
  return JSON.stringify(data, null, 2);
};

/**
 * Download file to user's device
 */
export const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Export data in specified format
 */
export const exportData = (
  data: any[],
  format: ExportFormat,
  filename: string,
  title?: string
) => {
  let content: string;
  let mimeType: string;
  let extension: string;
  
  switch (format) {
    case 'csv':
      content = convertToCSV(data);
      mimeType = 'text/csv;charset=utf-8;';
      extension = '.csv';
      break;
    
    case 'json':
      content = convertToJSON(data);
      mimeType = 'application/json;charset=utf-8;';
      extension = '.json';
      break;
    
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
  
  const fullFilename = `${filename}${extension}`;
  downloadFile(content, fullFilename, mimeType);
};

/**
 * Format date for export filenames
 */
export const formatDateForFilename = (date: Date = new Date()): string => {
  return date.toISOString().split('T')[0];
};
